import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, ProgressBar, Spinner } from "react-bootstrap";
import { FileText, Trash2, Upload, ExternalLink } from "lucide-react";
import QuickQuizService, { AnswerKeyFile } from "../../_services/QuickQuizService";

interface AnswerKeyModalProps {
  show: boolean;
  testId: number;
  testName?: string;
  onClose: () => void;
}

/**
 * Answer Key manager for a test (Quick Quiz Maker "Test View").
 * Teachers upload one or more PDF answer keys; students mapped to the test can
 * view them in ilearn. PDF only.
 */
const AnswerKeyModal: React.FC<AnswerKeyModalProps> = ({
  show,
  testId,
  testName,
  onClose,
}) => {
  const [files, setFiles] = useState<AnswerKeyFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await QuickQuizService.listAnswerKeys(testId);
      setFiles(list);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load answer keys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && testId) {
      loadFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, testId]);

  const handlePick = () => fileInputRef.current?.click();

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    // Reset so the same file can be re-selected later
    e.target.value = "";
    if (!file) return;

    if (file.type !== "application/pdf") {
      setError("Only PDF files are allowed for answer keys.");
      return;
    }

    setUploading(true);
    setProgress(0);
    setError(null);
    try {
      const res = await QuickQuizService.uploadAnswerKey(testId, file, (p) =>
        setProgress(p)
      );
      if (!res?.success) {
        setError(res?.message || "Upload failed");
      } else {
        await loadFiles();
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDelete = async (keyId: number) => {
    setDeletingId(keyId);
    setError(null);
    try {
      await QuickQuizService.deleteAnswerKey(testId, keyId);
      setFiles((prev) => prev.filter((f) => f.id !== keyId));
    } catch (e: any) {
      setError(e?.response?.data?.message || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const formatSize = (bytes?: number): string => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>
          Answer Key{testName ? ` — ${testName}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>
          Upload answer-key PDFs for this test. Students who are mapped to the
          test will be able to view them in their login.
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        <Button
          variant="primary"
          size="sm"
          onClick={handlePick}
          disabled={uploading}
          style={{ marginBottom: "12px" }}
        >
          {uploading ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              Uploading…
            </>
          ) : (
            <>
              <Upload size={15} style={{ marginRight: 6, marginTop: -2 }} />
              Upload PDF
            </>
          )}
        </Button>

        {uploading && (
          <ProgressBar
            now={progress}
            label={`${progress}%`}
            style={{ marginBottom: "12px" }}
          />
        )}

        {error && (
          <div
            style={{
              color: "var(--status-danger, #dc3545)",
              fontSize: "0.85rem",
              marginBottom: "10px",
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "16px" }}>
            <Spinner animation="border" size="sm" /> Loading…
          </div>
        ) : files.length === 0 ? (
          <div
            style={{
              color: "var(--text-muted)",
              fontSize: "0.9rem",
              padding: "12px 0",
            }}
          >
            No answer keys uploaded yet.
          </div>
        ) : (
          <div style={{ border: "1px solid var(--border-default)", borderRadius: 8 }}>
            {files.map((f, idx) => (
              <div
                key={f.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px 12px",
                  borderTop: idx === 0 ? "none" : "1px solid var(--border-default)",
                }}
              >
                <FileText size={18} style={{ color: "var(--brand-primary)", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontSize: "0.9rem",
                    }}
                    title={f.fileName}
                  >
                    {f.fileName}
                  </div>
                  {f.size ? (
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                      {formatSize(f.size)}
                    </div>
                  ) : null}
                </div>
                {f.url && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => window.open(f.url as string, "_blank")}
                  >
                    <ExternalLink size={14} style={{ marginRight: 4, marginTop: -2 }} />
                    View
                  </Button>
                )}
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDelete(f.id)}
                  disabled={deletingId === f.id}
                >
                  {deletingId === f.id ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <Trash2 size={14} />
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AnswerKeyModal;
